import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NavigationAdapter } from '../GlobalNav/adapters/navigation-adapter';
import { AppSwitcherAdapter } from '../GlobalNav/adapters/app-switcher-adapter';
import { isI18nLanguageReady, normalizeLanguageCode } from '../i18n';
import { fetchWrapper } from '../utils';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import { useAppSwitcherData } from './useAppSwitcherData';
import { useOneIdEnvironment } from '../hooks/useOneIdEnvironment';
import {
  AppsV1IdentityResponse,
  AppsV1NavigationSuccessResponse,
  GenesisUserInfoResponse,
  NavigationData,
} from '../types';
import { useQuery } from 'react-query';
import {
  getRevenueNavigationQueryParams,
  RevenueNavigationResponse,
  ZuoraRevenueStorageKeys,
} from './useRevenueNavigation';
import {
  PLATFORM_MENU_GROUP_ID,
  useCustomObjectMapping,
} from './useCustomObjectDefinition';
import { useAppsmithSolutions } from './useAppsmithSolutions';
import { useDebugMode } from './useDebugMode';
import { useRevenueOTRMenuCheck } from './useRevenueOTRMenu';
import { useVariant, Variant } from '../GlobalNav/hooks/useVariant';
import { useMultiOrgHierarchy } from './useMultiOrgHierarchy';
import { useIdentity } from './useIdentity';
import { isMultiOrgEnabled } from '../GlobalNav/MultiOrg/utils';
import { useAppSwitcherFlag } from '../flags';
import { isRevenueLocation } from '../utils/isRevenueEnvironment';
import { useRevenueIdentity } from './useRevenueIdentity';
import { useLocalStorageValue } from './useLocalStorageValue';
import { resolvePreferredLanguage } from '../utils/resolvePreferredLanguage';
import { useBillingLighthousePreference } from './useLighthousePreference';
// import { useUserPreferences } from './useUserPreferences'; // TODO (ZPY-458)

const CACHE_KEY = 'nav_data_cache';
const CACHE_DURATION = 3600000; // 1 hour

interface NavigationCache {
  transformedData: NavigationData;
  timestamp: number;
  dataHash?: string;
  // The resolved language the cached strings were rendered in. Cache is keyed
  // by tenant only, so this is the sole record of which language a hit holds.
  language?: string;
}

const computeStableHash = (value: unknown): string | null => {
  try {
    const json = JSON.stringify(value);
    let hash = 0;
    for (let i = 0; i < json.length; i += 1) {
      hash = (hash << 5) - hash + json.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString();
  } catch (err) {
    console.warn('Failed to hash navigation data:', err);
    return null;
  }
};

export const useNavigationData = (isNewZuoraExperienceEnabled?: boolean) => {
  const { i18n } = useTranslation();
  const hasTransformed = useRef(false);
  const appliedNewZuoraExperienceRef = useRef<boolean | undefined>(undefined);
  const { debugLog } = useDebugMode();
  const isNewZuoraExperienceEnabledForTransform =
    isNewZuoraExperienceEnabled ?? false;
  const [transformedData, setTransformedData] = useState<NavigationData | null>(
    null,
  );
  // Language the current transformedData was rendered in. Gated against
  // resolvedLanguage on the way out so a tenant-keyed cache hit from a previous
  // language never paints before it is rebuilt in the active language.
  const [displayedLanguage, setDisplayedLanguage] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCacheLoaded, setHasCacheLoaded] = useState(false);
  const [cacheMetadata, setCacheMetadata] = useState<NavigationCache | null>(
    null,
  );
  const [shouldRefreshFromApi, setShouldRefreshFromApi] = useState(false);

  const environment = useOneIdEnvironment();
  const variant = useVariant();
  const isBilling = variant === Variant.BILLING;
  const { data: sharedIdentityData } = useIdentity();
  const { revenueUserLanguage } = useRevenueIdentity();
  const isRevenueApp = useMemo(() => {
    return (
      isRevenueLocation(document.location) ||
      variant === Variant.ONE_ID_REVENUE ||
      variant === Variant.OTR
    );
  }, [variant]);
  // OneID Revenue profile role comes from host localStorage and may be written
  // after multi-role selection — after the first nav transform. Subscribe so
  // the data hash invalidates and profile re-transforms with the selected role.
  // The same value keys the sidemenu query so a late write also refetches menu.
  const isOneIdRevenue = variant === Variant.ONE_ID_REVENUE;
  const authUserRole = useLocalStorageValue(
    ZuoraRevenueStorageKeys.AuthUserRole,
    isOneIdRevenue,
  );

  const [isAppSwitcherEnabled] = useAppSwitcherFlag();
  const {
    data: appSwitcherData,
    isLoading: appSwitcherLoading,
    error: tenantsError,
  } = useAppSwitcherData();

  // There is something wrong when fetching app switcher data, but we still want to show the global nav
  const shouldSkipEntityRequirement =
    isAppSwitcherEnabled &&
    !!tenantsError &&
    !appSwitcherData &&
    !appSwitcherLoading;

  const {
    data: billingRawData,
    isLoading: billingLoading,
    error: billingError,
  } = useQuery({
    // resolvedLanguage is part of the key because /v1/navigation returns
    // localized menu labels (the transformer passes item.label through
    // untranslated), so a language switch must refetch rather than reuse the
    // previous locale's response.
    queryKey: ['billing-navigation', i18n.resolvedLanguage],
    queryFn: () =>
      fetchWrapper<AppsV1NavigationSuccessResponse>(
        NAVIGATION_ENDPOINTS.V1_NAVIGATION,
      ),
    enabled: isBilling,
    staleTime: 300000,
  });

  const { data: billingData } = useRevenueOTRMenuCheck(billingRawData);

  const {
    data: identityData,
    isLoading: identityLoading,
    error: identityError,
  } = useQuery({
    queryKey: ['identity-for-global-nav'],
    queryFn: () =>
      fetchWrapper<AppsV1IdentityResponse>(NAVIGATION_ENDPOINTS.V1_IDENTITY),
    staleTime: 300000,
  });

  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions-for-global-nav'],
    queryFn: () =>
      fetchWrapper<GenesisUserInfoResponse>(
        NAVIGATION_ENDPOINTS.GENESIS_USER_INFO,
      ),
    enabled: variant === Variant.BILLING || variant === Variant.OTR,
    staleTime: 300000,
  });

  const hasRevenueAccess = useMemo(() => {
    if (isRevenueApp && shouldSkipEntityRequirement) {
      return true;
    }
    if (appSwitcherData) {
      const { revenueEntities } = AppSwitcherAdapter.transformAppSwitcherData(
        appSwitcherData,
        identityData,
        variant,
      );
      return revenueEntities.length > 0;
    } else if (isRevenueApp && identityData) {
      const { entities: revenueEntities } =
        AppSwitcherAdapter.transformFromIdentityData(identityData);
      return revenueEntities.length > 0;
    }
    return false;
  }, [
    appSwitcherData,
    identityData,
    isRevenueApp,
    shouldSkipEntityRequirement,
  ]);

  const {
    data: revenueData,
    isLoading: revenueLoading,
    error: revenueError,
  } = useQuery({
    // resolvedLanguage keys this query for the same reason as billing: the
    // revenue side menu returns locale-sensitive labels, so a language switch
    // must refetch instead of reusing the prior locale's response.
    queryKey: [
      'revenue-navigation',
      environment?.revenuePrefix,
      i18n.resolvedLanguage,
      // Host writes auth_userrole after multi-role selection. Include it so
      // the sidemenu refetches once the selected role is available.
      authUserRole,
    ],
    queryFn: async () => {
      if (!environment?.revenuePrefix) {
        throw new Error('Revenue prefix not available');
      }
      const response = await fetchWrapper<{ items: any[] }>(
        `${
          environment.revenuePrefix
        }/api/revpro/secure/topBar/sidemenu?${getRevenueNavigationQueryParams().toString()}`,
      );
      return { items: response.items || [] } as RevenueNavigationResponse;
    },
    staleTime: 300000,
    enabled:
      variant !== Variant.BILLING &&
      hasRevenueAccess &&
      !!environment?.revenuePrefix,
    retry: 1,
  });

  const cacheKey = useMemo(() => {
    const currentTenantId =
      (isBilling &&
        `${identityData?.tenantId}_${identityData?.userProfileId}`) ||
      (isRevenueApp &&
        `${localStorage.getItem('ZRevenue_TenantID')}_${localStorage.getItem(
          'ZRevenue_UserID',
        )}`) ||
      'unknown';
    return `${CACHE_KEY}_${currentTenantId}`;
  }, [identityData?.tenantId]);

  useEffect(() => {
    const loadCache = () => {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) {
          setCacheMetadata(null);
          setHasCacheLoaded(true);
          return false;
        }

        const parsedCache: NavigationCache = JSON.parse(cached);

        if (Date.now() - parsedCache.timestamp < CACHE_DURATION) {
          setTransformedData(parsedCache.transformedData);
          setDisplayedLanguage(parsedCache.language ?? null);
          setCacheMetadata(parsedCache);
          setHasCacheLoaded(true);
          return true;
        } else {
          localStorage.removeItem(cacheKey);
          setCacheMetadata(null);
          setHasCacheLoaded(true);
          return false;
        }
      } catch (err) {
        console.warn('Failed to load navigation cache:', err);
        localStorage.removeItem(cacheKey);
        setCacheMetadata(null);
        setHasCacheLoaded(true);
        return false;
      }
    };

    setHasCacheLoaded(true);
    setTransformedData(null); // Clear old data when cache key changes
    setDisplayedLanguage(null);
    appliedNewZuoraExperienceRef.current = undefined;
    loadCache();
  }, [cacheKey]);

  const targetLanguage = useMemo(() => {
    // Keep the readiness gate aligned with QueryClientProvider.
    // sharedIdentityData is the same useIdentity() source QCP uses; identityData
    // is a separate nav-only query that can lag during story/app transitions.
    return normalizeLanguageCode(
      resolvePreferredLanguage({
        billingLanguage: sharedIdentityData?.language || identityData?.language,
        revenueUserLanguage,
      }),
    );
  }, [
    sharedIdentityData?.language,
    revenueUserLanguage,
    identityData?.language,
  ]);

  const isLanguageReady = useMemo(() => {
    return isI18nLanguageReady({
      i18n,
      targetLanguage,
    });
  }, [
    i18n.isInitialized,
    i18n.language,
    i18n.resolvedLanguage,
    i18n.services,
    (i18n as typeof i18n & { isLanguageChangingTo?: string })
      .isLanguageChangingTo,
    targetLanguage,
  ]);

  const { billingEntities, revenueEntities, allEntities } = useMemo(() => {
    if (appSwitcherLoading) {
      return { billingEntities: [], revenueEntities: [], allEntities: [] };
    }

    // If we have appSwitcherData, use the existing transformation
    if (appSwitcherData?.data) {
      const entities = AppSwitcherAdapter.transformAppSwitcherData(
        appSwitcherData,
        identityData,
        variant,
      );
      const currentTenantId =
        (isBilling && identityData?.tenantId) ||
        (isRevenueApp && localStorage.getItem('ZRevenue_TenantID')) ||
        'unknown';
      const findTenantRecursively = (
        items: typeof entities.allEntities,
      ): boolean => {
        return items?.some(
          (entity) =>
            entity.tenantId === currentTenantId ||
            (entity.children && findTenantRecursively(entity.children)),
        );
      };
      const findTenant = findTenantRecursively(entities?.allEntities);
      if (findTenant) {
        return entities;
      }
    }

    // If no appSwitcherData but we have identity data and we're in billing or OTR context
    if (identityData) {
      debugLog(
        'Creating billing entities from identity data for non-OneID user',
      );
      const { entities } =
        AppSwitcherAdapter.transformFromIdentityData(identityData);
      debugLog('Transformed identity data:', entities);
      return {
        billingEntities: isBilling ? entities : [],
        revenueEntities: isRevenueApp ? entities : [],
        allEntities: entities || [],
      };
    }

    // No usable data available
    debugLog('No appSwitcherData or identity data available');
    return { billingEntities: [], revenueEntities: [], allEntities: [] };
  }, [
    appSwitcherData,
    identityData,
    isBilling,
    isRevenueApp,
    variant,
    debugLog,
    appSwitcherLoading,
  ]);

  const { addSolutionMenuItems, isLoading: isAppsmithSolutionsLoading } =
    useAppsmithSolutions();

  const { data: multiOrgHierarchy, isLoading: isHierarchyLoading } =
    useMultiOrgHierarchy({
      enabled: isBilling && isMultiOrgEnabled(permissionsData),
    });

  const shouldLoadCustomObjects = useMemo(() => {
    const platformMenuGroupIndex =
      billingData?.menus?.findIndex(
        (item) => item.id === PLATFORM_MENU_GROUP_ID,
      ) ?? -1;
    return platformMenuGroupIndex >= 0;
  }, [billingData?.menus]);

  const { data: customObjectNavItems, isLoading: isCustomObjectLoading } =
    useCustomObjectMapping({
      enabled: shouldLoadCustomObjects,
    });

  const { isPaymentLinkEnabled, isLoading: isPaymentLinkConfigLoading } =
    useBillingLighthousePreference(isBilling);

  const isDataReady = useMemo(() => {
    if (
      appSwitcherLoading ||
      isAppsmithSolutionsLoading ||
      revenueLoading ||
      billingLoading ||
      permissionsLoading ||
      isHierarchyLoading ||
      identityLoading ||
      isCustomObjectLoading ||
      (isBilling && isPaymentLinkConfigLoading) ||
      !isLanguageReady
    ) {
      return false;
    }

    if (appSwitcherData || shouldSkipEntityRequirement) {
      if (isRevenueApp) {
        return !hasRevenueAccess || (hasRevenueAccess && !!revenueData);
      } else {
        return !!(billingData && identityData);
      }
    }

    if (isBilling) {
      const billingDataReady = !!(billingData && identityData);

      const hasBillingEntities = billingEntities.length > 0;

      return billingDataReady && hasBillingEntities;
    }

    // OTR
    if (isRevenueApp) {
      const revenueDataReady = !!(revenueData && identityData);
      return revenueDataReady && revenueEntities.length > 0;
    }

    return false;
  }, [
    appSwitcherData,
    isRevenueApp,
    hasRevenueAccess,
    revenueData,
    revenueLoading,
    billingData,
    identityData,
    isBilling,
    isAppsmithSolutionsLoading,
    billingEntities,
    revenueEntities,
    appSwitcherLoading,
    permissionsLoading,
    billingLoading,
    isHierarchyLoading,
    identityLoading,
    isCustomObjectLoading,
    shouldSkipEntityRequirement,
    isLanguageReady,
    isPaymentLinkConfigLoading,
  ]);

  // Use ref to preserve hash value when data is not ready
  const previousHashRef = useRef<string>('');

  const currentDataHash = useMemo(() => {
    if (!isDataReady) {
      // Return previous hash value instead of empty string
      return previousHashRef.current;
    }
    const newHash = computeStableHash({
      billingData,
      identityData,
      permissionsData,
      revenueData,
      appSwitcherData,
      customObjectNavItems,
      language: i18n.resolvedLanguage,
      reinvented: isNewZuoraExperienceEnabledForTransform,
      // OneID Revenue: auth_userrole is written by the host after multi-role
      // selection. Include it so a late write busts the 1h nav cache and
      // re-runs NavigationAdapter (which snapshots role into primary.user).
      authUserRole: isOneIdRevenue ? authUserRole : undefined,
      isPaymentLinkEnabled,
    });
    // Save the new hash for future reference
    previousHashRef.current = newHash ?? '';
    return newHash;
  }, [
    isDataReady,
    billingData,
    identityData,
    permissionsData,
    revenueData,
    appSwitcherData,
    customObjectNavItems,
    i18n.resolvedLanguage,
    isNewZuoraExperienceEnabledForTransform,
    isOneIdRevenue,
    authUserRole,
    isPaymentLinkEnabled,
  ]);

  // Memoise navigation adapter
  const navigationAdapter = useMemo(() => {
    return new NavigationAdapter(
      environment?.revenuePrefix || '/revenue',
      isRevenueApp,
      variant,
    );
  }, [variant]);

  useEffect(() => {
    if (!isDataReady || !cacheMetadata) {
      return;
    }

    // Determine if cache should be invalidated and whether to trigger API refresh
    const shouldInvalidate =
      !cacheMetadata.dataHash || // No hash in cache (legacy/corrupted)
      (currentDataHash && cacheMetadata.dataHash !== currentDataHash); // Hash mismatch

    if (!shouldInvalidate) {
      return;
    }

    // Invalidate cache
    localStorage.removeItem(cacheKey);
    setCacheMetadata(null);
    hasTransformed.current = false;

    // Only trigger API refresh if we have a valid hash to use for the new cache
    // (avoids infinite loop when hash generation fails)
    if (currentDataHash) {
      setShouldRefreshFromApi(true);
    }
  }, [cacheMetadata, currentDataHash, isDataReady, cacheKey]);

  // Re-transform when the New Zuora Experience flag resolves or changes after
  // an earlier transform (e.g. nav data was ready before user preferences returned).
  useEffect(() => {
    if (
      appliedNewZuoraExperienceRef.current !== undefined &&
      appliedNewZuoraExperienceRef.current !==
        isNewZuoraExperienceEnabledForTransform
    ) {
      hasTransformed.current = false;
      setShouldRefreshFromApi(true);
    }
  }, [isNewZuoraExperienceEnabledForTransform]);

  useEffect(() => {
    if ((transformedData && !shouldRefreshFromApi) || hasTransformed.current) {
      return;
    }

    if (!isDataReady || !hasCacheLoaded) {
      return;
    }

    const transformData = async () => {
      hasTransformed.current = true;
      setLoading(true);
      setError(null);

      try {
        const transformed = await navigationAdapter.transform(
          billingData,
          identityData,
          permissionsData,
          billingEntities,
          revenueEntities,
          allEntities,
          revenueData,
          multiOrgHierarchy,
          customObjectNavItems,
          addSolutionMenuItems,
          isNewZuoraExperienceEnabledForTransform,
          isPaymentLinkEnabled,
        );

        // When refreshing from API with existing cached data displayed,
        // compare the transformed navigation structure to avoid unnecessary re-render
        const oldNavHash = computeStableHash(transformedData);
        const newNavHash = computeStableHash(transformed);
        const shouldSkipUiUpdate =
          shouldRefreshFromApi &&
          transformedData &&
          oldNavHash != null &&
          newNavHash != null &&
          oldNavHash === newNavHash;

        // Record the language BEFORE publishing the data. In React 17 these
        // setStates are unbatched (we are past an await), so setTransformedData
        // triggers an intermediate render on its own — recording the language
        // first keeps that render from seeing fresh data with a stale language
        // and falsely re-flagging a stale-language rebuild.
        setDisplayedLanguage(i18n.resolvedLanguage);
        if (!shouldSkipUiUpdate) {
          setTransformedData(transformed);
        }
        appliedNewZuoraExperienceRef.current =
          isNewZuoraExperienceEnabledForTransform;
        setShouldRefreshFromApi(false);

        // Always update cache with latest data
        if (currentDataHash) {
          try {
            const cacheData: NavigationCache = {
              transformedData: transformed,
              timestamp: Date.now(),
              dataHash: currentDataHash,
              language: i18n.resolvedLanguage,
            };
            setCacheMetadata(cacheData);
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (cacheErr) {
            console.warn('⚠️ Failed to cache navigation data:', cacheErr);
          }
        } else {
          console.warn('⚠️ Skipping cache save: invalid data hash');
        }
      } catch (err) {
        console.error('❌ Error transforming navigation data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        hasTransformed.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    transformData();
  }, [
    isDataReady,
    hasCacheLoaded,
    transformedData,
    shouldRefreshFromApi,
    currentDataHash,
    isNewZuoraExperienceEnabledForTransform,
  ]);

  const criticalError = useMemo(() => {
    const firstError = tenantsError || billingError || identityError;

    if (firstError) {
      return firstError instanceof Error
        ? firstError.message
        : String(firstError);
    }
    return null;
  }, [tenantsError, billingError, identityError]);

  useEffect(() => {
    if (revenueError && hasRevenueAccess) {
      console.warn('⚠️ Revenue data failed to load:', revenueError);
    }
  }, [revenueError, hasRevenueAccess]);

  // Utility functions
  const clearCache = useCallback(() => {
    localStorage.removeItem(cacheKey);
    setTransformedData(null);
    setDisplayedLanguage(null);
    setHasCacheLoaded(false);
    setCacheMetadata(null);
    hasTransformed.current = false;
    appliedNewZuoraExperienceRef.current = undefined;
  }, [cacheKey, setDisplayedLanguage]);

  const refetch = useCallback(() => {
    localStorage.removeItem(cacheKey);
    setTransformedData(null);
    setDisplayedLanguage(null);
    setHasCacheLoaded(false);
    setError(null);
    setCacheMetadata(null);
    hasTransformed.current = false;
    appliedNewZuoraExperienceRef.current = undefined;

    setTimeout(() => setHasCacheLoaded(true), 0);
  }, [cacheKey]);

  useEffect(() => {
    if (!transformedData) return;
    debugLog('Navigation transformedData:', transformedData);
  }, [transformedData]);

  // The adapter bakes translated strings into transformedData, and the cache is
  // keyed by tenant only — so a hit can hold a previous language's nav. Expose
  // data only once the catalog is ready AND the rendered language matches the
  // active one; otherwise keep the skeleton up until it is rebuilt in-language.
  const isDisplayedLanguageCurrent =
    isLanguageReady && displayedLanguage === i18n.resolvedLanguage;

  return {
    transformedData: isDisplayedLanguageCurrent ? transformedData : null,
    loading: loading || !hasCacheLoaded || !isDisplayedLanguageCurrent,
    error: criticalError || error,
    hasRevenueAccess,
    revenueError: hasRevenueAccess ? revenueError : null,
    rawData: {
      appSwitcherData,
      billingData,
      identityData,
      permissionsData,
      revenueData,
    },
    clearCache,
    refetch,
  };
};
